import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ModeStandbyOutlined, AttachMoneyOutlined, WorkOutlineOutlined } from "@mui/icons-material";
import moment from "moment";

import { CardLayout, DeleteModal, LedgerModeToggle, MTable } from "../v3";
import { getLedgerTableColumns, getStatementTableColumns } from "../../pages/worker/constants";
import {
  DELETE_WORK,
  DELETE_WORKER_PAYMENT,
  EDIT_WORK,
  EDIT_WORKER_PAYMENT,
  EXPORT_WORKER_STATEMENT,
  VIEW_WORKER_LEDGER_MODE_STATEMENT,
} from "../../constants";
import { AccountService, AnalyticsService, FinanceService, HumanResourceService, ProjectService } from "../../services";
import { dateWithCurrentTime, derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { getAccountName } from "../../utils/formatters";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";
import { renameCashAccount } from "../../utils/accountUtils";

export const WorkerStatement = ({ name = "Worker", isGroupWorker = false, onSuccess = () => {}, reloadData }) => {
  const [workAndTransactions, setWorkAndTransactions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    isLedgerMode: isSavedLedgerMode,
    setLedgerMode: setIsLedgerMode,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "statement",
    defaults: { filter: "ALL", sortModel: [{ field: "date", sort: "desc" }] },
    filterValues: ["ALL", "work", "transaction"],
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: "",
    analyticsId: null,
  });

  const { id: workerId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasEditWork = useHasPermission(EDIT_WORK);
  const hasDeleteWork = useHasPermission(DELETE_WORK);
  const hasEditPayment = useHasPermission(EDIT_WORKER_PAYMENT);
  const hasDeletePayment = useHasPermission(DELETE_WORKER_PAYMENT);
  const hasExportWork = useHasPermission(EXPORT_WORKER_STATEMENT);
  const hasLedgerAccess = useHasPermission(VIEW_WORKER_LEDGER_MODE_STATEMENT);
  // A remembered ledger toggle never outlives the permission.
  const isLedgerMode = hasLedgerAccess && isSavedLedgerMode;

  const fetchData = useCallback(
    async (statementFilters = {}) => {
      try {
        setLoading(true);
        const workerAnalytics = isLedgerMode
          ? await AnalyticsService.getWorkerLedger({ ...statementFilters, workerId })
          : await AnalyticsService.getWorkerAnalytics({ ...statementFilters, workerId });
        setWorkAndTransactions(workerAnalytics?.data?.rows);
        setTotalCount(workerAnalytics?.data?.count);
        // gets the unique project and account ids from the work response to get the project and account names
        const projectIds = Array.from(new Set(workerAnalytics?.data?.rows?.map((item) => item.projectId)));
        const accountIds = Array.from(new Set(workerAnalytics?.data?.rows?.map((item) => item.fromAccountId)));

        if (projectIds.length > 0) {
          const projectResponse = await ProjectService.getProjectList({
            ids: projectIds.join(","),
            limit: projectIds.length,
            includeDeleted: true,
          });
          setProjects(projectResponse?.data?.rows);
        }
        if (accountIds.length > 0) {
          const accountResponse = await AccountService.getAccounts({
            ids: accountIds.join(","),
            limit: accountIds.length,
            includeDeleted: true,
            includeOwnerDetails: true,
          });
          setAccounts(accountResponse?.data?.rows);
        }
      } catch (error) {
        toast.error("Failed to fetch work history");
        console.error("Error fetching work history:", error);
      } finally {
        setLoading(false);
      }
    },
    [workerId, isLedgerMode],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput to report initial dates

    fetchData({
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
      ...(!isLedgerMode && {
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        ...(selectedFilter !== "ALL" && { type: selectedFilter }),
        description: searchText,
      }),
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, dateRange, reloadData, isLedgerMode]);

  const handleLedgerModeToggle = () => {
    setIsLedgerMode((prev) => !prev);
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    setDateRange({
      startDate: dateWithCurrentTime(moment(selection.startDate).startOf("day")),
      endDate: dateWithCurrentTime(moment(selection.endDate).endOf("day")),
    });

    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[selectedFilter] || `Works & Payments : ${name}`;
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title={pdfTitle} columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: pdfTitle,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params, event) => {
    if (params.row.type === "transaction") navigate(`/worker/${workerId}/payment/${params.row.itemId}`);
    else if (params.row.type === "work") navigate(`/worker/${workerId}/work/${params.row.itemId}`);
  };

  const openDeleteModal = (entity) => {
    setDeleteModal({
      isOpen: true,
      type: entity?.type,
      analyticsId: entity?.itemId,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      type: "",
      analyticsId: null,
    });
  };

  const handleDelete = async () => {
    try {
      if (deleteModal.type === "transaction") await FinanceService.deleteTransaction(deleteModal.analyticsId);
      else if (deleteModal.type === "work") await HumanResourceService.deleteWork(deleteModal.analyticsId);

      toast.success(`${deleteModal.type} deleted successfully`);
      onSuccess?.();
    } catch (error) {
      toast.error(`Failed to delete ${deleteModal.type}`);
      console.error("🚀 ~ deleteWorkerAnalytics ~ error:", error);
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = async (entityData, actionType) => {
    switch (actionType) {
      case "edit":
        if (entityData.type === "transaction") navigate(`/transaction/edit/${entityData.itemId}?workerId=${workerId}`);
        else if (entityData.type === "work") navigate(`/edit-work/${entityData.itemId}`);
        break;
      case "delete":
        openDeleteModal(entityData);
        break;
      default:
        break;
    }
  };

  const columnHeaders = isLedgerMode
    ? getLedgerTableColumns(handleActions, hasEditWork, hasDeleteWork, hasEditPayment, hasDeletePayment, isGroupWorker)
    : getStatementTableColumns(
        handleActions,
        hasEditWork,
        hasDeleteWork,
        hasEditPayment,
        hasDeletePayment,
        selectedFilter,
        isGroupWorker,
      );

  const ledgerModeToggle = hasLedgerAccess ? (
    <LedgerModeToggle isLedgerMode={isLedgerMode} onToggle={handleLedgerModeToggle} />
  ) : null;

  const filterOptions = [
    { label: "All", value: "ALL", icon: <ModeStandbyOutlined /> },
    { label: "Work", value: "work", icon: <WorkOutlineOutlined /> },
    { label: "Payment", value: "transaction", icon: <AttachMoneyOutlined /> },
  ];

  const pdfTitleMap = {
    ALL: `Works & Payments : ${name}`,
    work: `Works : ${name}`,
    transaction: `Payments : ${name}`,
  };

  const rows = workAndTransactions.map((transaction, index) => {
    const project = projects.find((project) => project.id === transaction.projectId);
    const account = accounts.find((account) => account.id === transaction.fromAccountId);
    const isCashOrUser = ["CASH", "USER"].includes(account?.type);

    const accountDisplayName =
      (isCashOrUser && renameCashAccount(account, user, user.company.ownerUserId)?.name) ||
      getAccountName(account?.type, account);
    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: index,
      itemId: transaction.id,
      date: moment(transaction.date).format("DD MMM YYYY"),
      projectName: transaction.fromAccountId ? accountDisplayName : project?.name,
      remarks: transaction.description,
      work: transaction.type === "work" ? (transaction.isGroupWork ? transaction.amount : transaction.wage) : null,
      overtime:
        transaction.type === "work" && !transaction.isGroupWork
          ? transaction.overtimeHours && transaction.overtimeHours * transaction.overtimeRate
          : null,
      amount: transaction.type === "transaction" ? transaction.amount : null,
      balance: transaction.balance,
      type: transaction.type,
      isGroupWork: transaction.isGroupWork,
      groupWork: transaction.isGroupWork ? "Group" : "Individual",
      meta: transaction.meta,
    };
  });

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <>
      <CardLayout>
        <MTable
          // Filter data
          selectedFilter={selectedFilter}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          showFilterButton
          disableFilterButton={isLedgerMode}
          // Search data
          placeholder="Search by description"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          disableSearchBar={isLedgerMode}
          startAction={ledgerModeToggle}
          // Date range data
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          // Export data
          showExportButton={hasExportWork}
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          defaultSortModel={DEFAULT_SORT}
          disableSorting={isLedgerMode}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message={`Want to delete this
          ${deleteModal.type === "work" ? "Work" : "Transaction"}`}
      />
    </>
  );
};
